import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref } from "vue";
import { noteStorageLimit } from "@/shared/billing/storage-limit";
import { fetchWithSession, HttpError, request } from "@/shared/http/client";
import { beginLoading } from "@/shared/http/loading-bar";
import { describeServerError } from "@/shared/i18n/describe-error";

/**
 * The agents' filesystem — and everything else this workspace is holding.
 *
 * Notes, drafts, extracted data — whatever an agent needed to put somewhere between
 * turns. Worth a screen because it is the only place an agent's working memory is
 * legible: when the builder claims it wrote down a catalogue, this is where you check.
 * The same index also holds every attachment, signature and inbound picture, which is
 * why the filters below are by ORIGIN and not only by path.
 *
 * Writes are versioned and deletes are soft, so nothing here is destructive.
 */
export interface FileEntry {
	readonly id: string;
	readonly path: string;
	/**
	 * O nome que a pessoa reconhece — o último segmento SEM o sufixo de unicidade.
	 *
	 * Vem do SERVIDOR (`displayNameOf`, irmã de `buildPath`) e não é calculado
	 * aqui: quem põe os doze hexadecimais à frente do nome é ele, e desfazê-los no
	 * painel seria a mesma regra escrita duas vezes, em dois idiomas.
	 */
	readonly name: string;
	/** Quem é o dono, em palavras. `null` quando não há nome a resolver. */
	readonly ownerLabel: string | null;
	readonly mimeType: string;
	readonly sizeBytes: number;
	readonly version: number;
	readonly contentHash: string;
	/** When THIS version was written. Every write is a new version, so it is also the
	 * modification time of what you are looking at. */
	readonly createdAt: string;
	readonly ownerKind: string | null;
	readonly ownerId: string | null;
	readonly purpose: string | null;
	readonly originUrl: string | null;
	/** Lines matching a `grep`, when the list was filtered by content. */
	readonly matches?: unknown;
}

/** One bucket of the usage answer. */
export interface UsageSlice {
	readonly key: string;
	readonly files: number;
	readonly bytes: number;
}

export interface FileUsage {
	readonly total: UsageSlice;
	readonly byPurpose: UsageSlice[];
	readonly byOwnerKind: UsageSlice[];
}

/**
 * Everything the server can narrow a listing by.
 *
 * `ownerKind`, `purpose` and `mime` were already accepted by `GET /v3/files` and the
 * panel simply never sent them, so "só as anotações" and "só as imagens" were
 * questions the server could answer and no screen could ask.
 */
export interface FileFilters {
	readonly prefix: Ref<string>;
	/**
	 * Parte do NOME, em qualquer lugar do caminho — outra pergunta que não o `grep`.
	 *
	 * O `grep` lê os BYTES: para um PDF, um JPEG ou um contrato assinado ele nunca
	 * casa. Procurar "o contrato da Joana" é procurar pelo nome, e só este campo faz
	 * isso.
	 */
	readonly name?: Ref<string>;
	readonly grep: Ref<string>;
	readonly ownerKind?: Ref<string>;
	readonly purpose?: Ref<string>;
	/** A PREFIX of the mime type: `image/` is "só imagens", in one condition. */
	readonly mime?: Ref<string>;
	/**
	 * O nome do DONO — o "Nome do contato" da tela antiga.
	 *
	 * Terceira busca, e a terceira pergunta: `name` procura no caminho, `grep`
	 * dentro dos bytes, e esta procura na PESSOA. Um anexo que chegou por uma
	 * conversa não tem o nome de ninguém no caminho nem dentro do arquivo.
	 */
	readonly ownerName?: Ref<string>;
	/** Só o que pertence a alguém. */
	readonly hasOwner?: Ref<boolean>;
	/** `recent` é o padrão do servidor; a tela o diz por escrito mesmo assim. */
	readonly sort?: Ref<"recent" | "name" | "size">;
	readonly dir?: Ref<"asc" | "desc">;
	readonly since?: Ref<string>;
	readonly until?: Ref<string>;
	readonly limit?: Ref<number>;
	readonly offset?: Ref<number>;
}

export const fileKeys = {
	list: (params: string) => ["files", "list", params] as const,
	content: (path: string) => ["files", "content", path] as const,
	history: (path: string) => ["files", "history", path] as const,
	usage: ["files", "usage"] as const,
};

/**
 * The querystring the server actually receives — and the cache key.
 *
 * Two filter sets that produce the same query ARE the same request; keying by the
 * parts instead would cache them apart for no reason.
 */
function queryOf(input: FileFilters): string {
	const params = new URLSearchParams();
	const put = (key: string, value: string | undefined): void => {
		if (value?.trim()) params.set(key, value.trim());
	};
	put("prefix", input.prefix.value);
	put("name", input.name?.value);
	put("grep", input.grep.value);
	put("ownerKind", input.ownerKind?.value);
	put("purpose", input.purpose?.value);
	put("mime", input.mime?.value);
	put("ownerName", input.ownerName?.value);
	put("sort", input.sort?.value);
	put("dir", input.dir?.value);
	put("since", input.since?.value);
	put("until", input.until?.value);
	// "1" e não "true": é o que o servidor lê, e um booleano numa querystring é
	// uma string de qualquer jeito — a que ele entende está escrita aqui.
	if (input.hasOwner?.value) params.set("hasOwner", "1");
	if (input.limit?.value) params.set("limit", String(input.limit.value));
	if (input.offset?.value) params.set("offset", String(input.offset.value));
	return params.toString();
}

/**
 * Uma página, e o tamanho do filtro inteiro.
 *
 * `total` conta o filtro no BANCO. O `grep` roda depois, sobre a página — então com
 * um `grep` escrito o total é quantos arquivos foram procurados, não quantos casaram,
 * e a tela para de paginar enquanto ele estiver lá.
 */
export function useFiles(input: FileFilters) {
	const query = computed(() => queryOf(input));
	return useQuery({
		queryKey: computed(() => fileKeys.list(query.value)),
		queryFn: () =>
			request<{ items: FileEntry[]; total: number }>(
				`/v3/files${query.value ? `?${query.value}` : ""}`,
			),
	});
}

/** Bytes and counts by purpose and by origin — what is taking the space. */
export function useFileUsage(enabled: Ref<boolean>) {
	return useQuery({
		queryKey: fileKeys.usage,
		enabled,
		queryFn: () => request<{ usage: FileUsage }>("/v3/files/usage"),
		select: (data) => data.usage,
		staleTime: 60_000,
	});
}

export function useFileContent(path: Ref<string | null>) {
	return useQuery({
		queryKey: computed(() => fileKeys.content(path.value ?? "")),
		enabled: computed(() => !!path.value),
		queryFn: () =>
			request<{ file: FileEntry; content: string }>(
				`/v3/files/content?path=${encodeURIComponent(path.value ?? "")}`,
			),
	});
}

/** Every version of one path — the audit trail behind whatever it says today. */
export function useFileHistory(path: Ref<string | null>) {
	return useQuery({
		queryKey: computed(() => fileKeys.history(path.value ?? "")),
		enabled: computed(() => !!path.value),
		queryFn: () =>
			request<{ versions: FileEntry[] }>(
				`/v3/files/history?path=${encodeURIComponent(path.value ?? "")}`,
			),
	});
}

export function useWriteFile() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { path: string; content: string; mimeType?: string }) =>
			request<{ file: FileEntry; unchanged: boolean }>("/v3/files", {
				method: "POST",
				body: input,
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["files"] }),
	});
}

const API_BASE = import.meta.env.VITE_API_BASE ?? "";
/**
 * Long enough for the largest file a channel takes (100 MB) on a slow uplink:
 * at 2 Mbit/s that is close to seven minutes.
 */
const UPLOAD_TIMEOUT_MS = 600_000;

/**
 * Sending a file the browser already holds.
 *
 * Multipart, and therefore NOT through `request`: that wrapper JSON-stringifies every
 * body, which is right for the rest of the API and impossible for a `FormData`. Same
 * escape hatch, and the same reason, as the streamed agent turn — `getAuthToken`
 * exists for exactly the callers that cannot use the wrapper.
 *
 * The ONLY road for bytes from the panel. `upload-port.ts` used to send base64 in
 * JSON, and the server's JSON parser stops at 20 MB: a 16 MB video is ~21 MB in
 * base64, so every attachment over ~15 MB in the inbox died before reaching the
 * upload route, whatever the channel accepted.
 */
export async function postUpload(input: {
	blob: Blob;
	filename?: string;
	ownerKind: string;
	ownerId?: string;
	purpose: string;
}): Promise<{ file: FileEntry; url: string }> {
	const form = new FormData();
	// The name rides on the part AND as a field: a recorded voice note is a Blob
	// with no name of its own, and the field is what the server reads first.
	form.append("file", input.blob, input.filename ?? (input.blob as File).name ?? "arquivo");
	form.append("ownerKind", input.ownerKind);
	if (input.ownerId) form.append("ownerId", input.ownerId);
	form.append("purpose", input.purpose);
	if (input.filename) form.append("filename", input.filename);
	if (input.blob.type) form.append("mimeType", input.blob.type);

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
	// Multipart não passa por `request`: a barra do topo acende aqui, que é a
	// espera mais longa do painel e a que mais pedia um sinal de vida.
	const endLoading = beginLoading("http");
	let failed = true;
	try {
		// No Content-Type header on purpose: only the browser knows the multipart
		// boundary it just generated, so setting one here breaks the parse.
		const res = await fetchWithSession(`${API_BASE}/v3/files/upload`, {
			method: "POST",
			body: form,
			credentials: "include",
			signal: controller.signal,
		});
		const text = await res.text();
		const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};
		if (!res.ok) {
			const refused = new HttpError(
				res.status === 413 || res.status === 400 ? "validation" : "server",
				typeof payload.message === "string" ? payload.message : `falhou com ${res.status}`,
				res.status,
				undefined,
				payload,
			);
			// Armazenamento cheio: quem responde é o diálogo da moldura, com a oferta.
			// A barra não fica vermelha (é uma recusa com código, como em `paintsRed`) e
			// quem chama mostra `e.message` — a frase traduzida, não o inglês do log.
			if (noteStorageLimit(refused)) {
				failed = false;
				throw new HttpError(
					"validation",
					describeServerError(refused),
					res.status,
					undefined,
					payload,
				);
			}
			throw refused;
		}
		failed = false;
		return payload as unknown as { file: FileEntry; url: string };
	} finally {
		clearTimeout(timer);
		endLoading(failed);
	}
}

export function useUploadFile() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { file: File; ownerKind: string; purpose: string }) =>
			postUpload({ blob: input.file, ownerKind: input.ownerKind, purpose: input.purpose }),
		onSuccess: () => client.invalidateQueries({ queryKey: ["files"] }),
	});
}

/**
 * Where a file is served from. Nobody builds a URL by hand (I-S4).
 *
 * `download` é PEDIDO AO SERVIDOR e nunca colado na URL que ele devolveu: no
 * driver de objeto a assinatura cobre os cabeçalhos da resposta, então
 * `...&download=1` grudado depois invalida a assinatura inteira. São duas
 * respostas diferentes para o mesmo arquivo, e quem as produz é ele.
 */
export function fileUrl(id: string, download = false): Promise<{ url: string }> {
	return request<{ url: string }>(`/v3/files/${id}/url${download ? "?download=1" : ""}`);
}

/** Soft delete: the path stops resolving, the bytes and history survive. */
export function useDeleteFile() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (path: string) =>
			request<{ deleted: boolean }>(`/v3/files?path=${encodeURIComponent(path)}`, {
				method: "DELETE",
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["files"] }),
	});
}

/**
 * The same soft delete, for a selection.
 *
 * Answers with the paths that actually stopped resolving, so a grid that sent forty
 * can say which ones are still there instead of a count nobody can act on.
 */
export function useDeleteFiles() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (paths: string[]) =>
			request<{ deleted: string[] }>("/v3/files/delete", { method: "POST", body: { paths } }),
		onSuccess: () => client.invalidateQueries({ queryKey: ["files"] }),
	});
}
